import type { CSSProperties } from 'react';

/**
 * Lifecycle phase of a stack entry. A modal that is animating out via
 * AnimatePresence stays `Closing` (keeping its visual z-index + backdrop
 * selection) until its exit completes, so its blur never drops behind a lower
 * modal mid-animation. Only `Open` entries count for depth / pop / dismissal.
 */
export enum ModalEntryPhase {
  Open = 'open',
  Closing = 'closing',
}

/**
 * Appearance of the single shared backdrop the provider renders behind the
 * topmost backdrop-wanting modal. `null` (on a registration) means the entry
 * participates in the stack but contributes no shared backdrop (popovers,
 * message modal, control-only overlays).
 */
export interface ModalBackdropConfig {
  opacity: number;
  blur: number;
  useGradient: boolean;
  topOffset?: number;
  animationDuration?: number;
  style?: CSSProperties;
}

export interface ModalEntry {
  id: string;
  onClose: () => void;
  backdrop: ModalBackdropConfig | null;
  phase: ModalEntryPhase;
}

export interface ModalStackControl {
  depth: number;
  pop: (id?: string) => boolean;
  closeAll: () => number;
  closeN: (count: number) => number;
  getDepthCount: () => number;
  resolveToDepth: (targetDepth: number) => number;
}

export interface ModalStackContextValue extends ModalStackControl {
  stack: ModalEntry[];
  push: (entry: ModalEntry) => void;
  registerModal: (registration: ModalEntry) => void;
  markClosing: (id: string) => void;
  unregisterModal: (id: string) => void;
}

export interface ModalLayerZIndices {
  backdrop: number;
  underlay: number;
  content: number;
}

export interface UseRegisterModalInput {
  id: string;
  /** The modal wants to be on the stack (its own `visible`). */
  visible: boolean;
  onClose: () => void;
  /** Shared-backdrop appearance, or `null` for overlays that contribute none. */
  backdrop: ModalBackdropConfig | null;
  /**
   * When true (modals with an exit animation), going invisible marks the entry
   * `Closing` and holds its visual layer until `completeExit()` is called from
   * AnimatePresence.onExitComplete — so its blur never drops behind a lower
   * modal mid-animation. When false (popovers / control-only), it unregisters
   * immediately on invisible.
   */
  holdUntilExitComplete?: boolean;
}

export interface ModalRegistration {
  /** z-index for the modal's overlay/content layer. */
  contentZIndex: number;
  /** z-index for a decorative layer between the shared backdrop and content. */
  underlayZIndex: number;
  /** True iff this entry is the topmost Open entry in the stack. */
  isTopOpen: boolean;
  /** Release the entry once its exit animation has finished. */
  completeExit: () => void;
}
